import * as React from 'react';
import {useCallback} from 'react';
import * as _ from 'lodash';
import {ExtendedWrappedFieldProps} from '../FormFields';
import {WrappedFieldProps} from 'redux-form';
import GridFormRow from '../GridFormRow';

export type ObjectFormInnerComponentProps<P extends WrappedFieldProps> = {
  component: React.ComponentType<P>;
  props?: Partial<P>;
};

interface ObjectFormComponentProps<PLeft extends WrappedFieldProps, PRight extends WrappedFieldProps>
  extends ExtendedWrappedFieldProps {
  left: ObjectFormInnerComponentProps<PLeft>;
  right: ObjectFormInnerComponentProps<PRight>;
}

function getValue(eventOrValue) {
  if (eventOrValue && eventOrValue.target) {
    eventOrValue = eventOrValue.target.value;
  }
  return eventOrValue;
}

export default function ObjectFormComponent<
  PLeft extends WrappedFieldProps = WrappedFieldProps,
  PRight extends WrappedFieldProps = WrappedFieldProps
>({meta, input: {value, onChange, onBlur, ...input}, left, right}: ObjectFormComponentProps<PLeft, PRight>) {
  let leftValue: string = null;
  let rightValue: string[] | string;
  let LeftComponent: React.ComponentType<WrappedFieldProps>, leftProps: Partial<PLeft>;
  ({component: LeftComponent, props: leftProps} = left);
  let RightComponent: React.ComponentType<WrappedFieldProps>, rightProps: Partial<PRight>;
  ({component: RightComponent, props: rightProps} = right);
  if (!_.isEmpty(value)) {
    const parsedValue = JSON.parse(value);
    leftValue = _.first(_.keys(parsedValue));
    rightValue = parsedValue[leftValue];
  }
  const handleLeftChange = useCallback(
    value => {
      value = getValue(value);
      onChange(value ? JSON.stringify({[value]: []}) : value);
    },
    [onChange]
  );
  const handleRightChange = useCallback(
    value => {
      value = getValue(value);
      onChange(JSON.stringify({[leftValue]: value}));
    },
    [leftValue, onChange]
  );
  const handleBlur = useCallback(() => {
    if (onBlur) {
      return onBlur(value);
    }
  }, [value, onBlur]);
  return (
    <GridFormRow
      left={
        <LeftComponent
          meta={_.omit(meta, 'error')}
          input={{
            value: leftValue,
            onChange: handleLeftChange,
          }}
          {...leftProps}
        />
      }
      right={
        <RightComponent
          meta={meta}
          disabled={_.isEmpty(leftValue)}
          input={{...input, value: rightValue, onChange: handleRightChange, onBlur: handleBlur}}
          {...rightProps}
        />
      }
    />
  );
}
